import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Eye, Loader2, RefreshCw } from "lucide-react";
import OperatorConsoleShell from "../components/operator/OperatorConsoleShell";
import AudienceAnalyticsPanel from "../components/operator/AudienceAnalyticsPanel";
import { selectAppUser } from "../redux/appAuthSlice";
import {
  clearHouseViewers,
  fetchHouseViewers,
  fetchOperatorDashboard,
} from "../redux/operatorConsoleSlice";
import { formatRelative } from "../utils/bettorsDeskHelpers";
import { buildTimelineSections } from "../utils/activityHelpers";
import {
  compactNumber,
  formatViewerCount,
  normalizeViewerPulse,
  viewerViewedDateKey,
} from "../utils/viewersDeskHelpers";
import "../bettors-desk.css";
import "../audience-desks.css";

const ViewersDesk = () => {
  const { houseId } = useParams();
  const dispatch = useDispatch();
  const userId = useSelector(selectAppUser)?._id;

  const {
    houses,
    dashboard,
    dashboardHouseId,
    houseViewers,
    houseViewersPagination,
    houseViewersPulse,
    houseViewersStatus,
    houseViewersError,
    loadingMoreHouseViewers,
  } = useSelector((s) => s.operatorConsole);

  const [refreshing, setRefreshing] = useState(false);

  const bet = useMemo(
    () => houses.find((h) => String(h._id) === String(houseId)),
    [houses, houseId]
  );

  useEffect(() => {
    if (houseId) dispatch(fetchOperatorDashboard(houseId));
  }, [dispatch, houseId]);

  const listParams = useMemo(
    () => ({
      houseId,
      operatorUserId: userId,
      page: 1,
      limit: 40,
      append: false,
    }),
    [houseId, userId]
  );

  const reload = useCallback(() => {
    if (!houseId || !userId) return;
    dispatch(fetchHouseViewers(listParams));
  }, [dispatch, houseId, userId, listParams]);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    return () => dispatch(clearHouseViewers());
  }, [dispatch]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await Promise.all([
        dispatch(fetchHouseViewers(listParams)).unwrap(),
        dispatch(fetchOperatorDashboard(houseId)).unwrap(),
      ]);
    } finally {
      setRefreshing(false);
    }
  };

  const handleLoadMore = () => {
    if (!houseId || !userId || loadingMoreHouseViewers || !houseViewersPagination?.hasNextPage) {
      return;
    }
    dispatch(
      fetchHouseViewers({
        houseId,
        operatorUserId: userId,
        page: (houseViewersPagination.page || 1) + 1,
        limit: houseViewersPagination.limit || 40,
        append: true,
      })
    );
  };

  const sections = useMemo(
    () => buildTimelineSections(houseViewers, viewerViewedDateKey),
    [houseViewers]
  );

  const pulseLoaded =
    houseViewersStatus === "succeeded" && houseViewersPulse != null;

  const pulse = useMemo(
    () => normalizeViewerPulse(houseViewersPulse),
    [houseViewersPulse]
  );

  const pulseLoading =
    houseViewersStatus === "loading" && !houseViewers.length && !pulseLoaded;

  const dashboardReady =
    dashboard && String(dashboardHouseId) === String(houseId) ? dashboard : null;

  const headerSubtitle = useMemo(() => {
    const houseName = bet?.name || dashboard?.houseName || "House";
    if (houseViewersStatus === "loading" && !houseViewers.length) {
      return houseName;
    }
    if (!pulseLoaded) {
      return `${houseName} · profile views`;
    }
    const unique = pulse.uniqueViewers ?? "—";
    return `${houseName} · ${formatViewerCount(pulse.totalViews)} · ${compactNumber(unique)} unique`;
  }, [
    bet?.name,
    dashboard?.houseName,
    houseViewersStatus,
    houseViewers.length,
    pulseLoaded,
    pulse.totalViews,
    pulse.uniqueViewers,
  ]);

  if (!houseId) {
    return (
      <OperatorConsoleShell activeSection="viewers" mainClassName="oc-desk-main">
        <div className="bd-page bd-audience-desk">
          <div className="bd-loading">Missing house.</div>
        </div>
      </OperatorConsoleShell>
    );
  }

  return (
    <OperatorConsoleShell activeSection="viewers" mainClassName="oc-desk-main">
      <div className="bd-page bd-audience-desk">
      <header className="bd-header bd-audience-header">
        <div className="bd-header-copy">
          <p className="bd-eyebrow">
            <Eye size={9} />
            Viewer desk
          </p>
          <span className="bd-header-title">
            {headerSubtitle}
            {pulseLoaded
              ? ` · ${compactNumber(pulse.repeatViewers)} repeat${
                  pulse.latestViewedAt
                    ? ` · last ${formatRelative(pulse.latestViewedAt)}`
                    : ""
                }`
              : ""}
          </span>
        </div>
        <button
          type="button"
          className="bd-icon-btn"
          onClick={handleRefresh}
          disabled={refreshing || houseViewersStatus === "loading"}
          aria-label="Refresh"
        >
          <RefreshCw size={13} className={refreshing ? "animate-spin" : ""} />
        </button>
      </header>

      <div className="bd-desk-body">
      <div className="bd-bettors-command bd-viewers-command">
        <aside className="bd-roster-rail">
          <section className="bd-roster bd-roster-rail-scroll bd-roster-rail-scroll-full">
            <header className="bd-rail-head">
              <h2>Timeline</h2>
              <span className="tabular-nums">{houseViewers.length}</span>
            </header>

            {houseViewersStatus === "loading" && !houseViewers.length && (
              <div className="bd-loading bd-loading-compact">
                <Loader2 size={18} className="animate-spin" />
                <span>Loading…</span>
              </div>
            )}

            {houseViewersError && !houseViewers.length && (
              <div className="bd-loading bd-loading-compact">
                <p>{String(houseViewersError)}</p>
                <button type="button" onClick={reload}>
                  Retry
                </button>
              </div>
            )}

            {!houseViewers.length && houseViewersStatus === "succeeded" && (
              <p className="bd-empty bd-empty-compact">No viewer events yet.</p>
            )}

            {sections.map((section) => (
              <div key={section.dateKey} className="bd-day">
                <h3 className="bd-day-head">
                  <span>{section.title}</span>
                  <span className="bd-day-line" aria-hidden />
                </h3>
                <ul className="bd-roster-list">
                  {section.data.map((row, index) => (
                    <li key={`${row.userId}-${row.viewedAt}-${index}`}>
                      <article className="bd-roster-row bd-viewer-row">
                        <span className="bd-roster-avatar">
                          {row.username?.[0]?.toUpperCase() || "?"}
                        </span>
                        <div className="bd-roster-copy">
                          <strong>{row.username || "Viewer"}</strong>
                        </div>
                        <div className="bd-roster-side bd-viewer-side">
                          <span className="bd-viewer-tag">View</span>
                          <em className="tabular-nums">
                            {row.viewedAt ? formatRelative(row.viewedAt) : "—"}
                          </em>
                        </div>
                      </article>
                    </li>
                  ))}
                </ul>
              </div>
            ))}

            {houseViewersPagination?.hasNextPage ? (
              <button
                type="button"
                className="bd-load-more bd-load-more-compact"
                onClick={handleLoadMore}
                disabled={loadingMoreHouseViewers}
              >
                {loadingMoreHouseViewers ? (
                  <>
                    <Loader2 size={13} className="animate-spin" />
                    Loading…
                  </>
                ) : (
                  "Load more"
                )}
              </button>
            ) : null}
          </section>
        </aside>

        <div className="bd-bettors-workspace">
          <AudienceAnalyticsPanel
            layout="workspace"
            pulse={pulse}
            loading={pulseLoading}
            dashboard={dashboardReady}
          />
        </div>
      </div>
      </div>
      </div>
    </OperatorConsoleShell>
  );
};

export default ViewersDesk;
